import {
  MachineAction,
  MachineDefinition,
  MachineGuard,
  MachineServiceFunction,
  MachineStateTagsNode,
} from './types';

type StrictTransition<
  TState extends string,
  TAction extends string,
  TGuard extends string,
> =
  | NoInfer<TState>
  | {
      target?: NoInfer<TState>;
      actions?: NoInfer<TAction>[];
      cond?: NoInfer<TGuard>;
    };

type StrictTransitions<
  TEvent extends { type: string },
  TState extends string,
  TAction extends string,
  TGuard extends string,
> = Partial<
  Record<
    TEvent['type'],
    | StrictTransition<TState, TAction, TGuard>
    | StrictTransition<TState, TAction, TGuard>[]
  >
>;

type StrictStateNode<
  TContext,
  TEvent extends { type: string },
  TState extends string,
  TAction extends string,
  TGuard extends string,
  TService extends string,
> = {
  on?: StrictTransitions<TEvent, TState, TAction, TGuard>;
  invoke?: {
    src: NoInfer<TService> | MachineServiceFunction<TContext, TEvent>;
    onDone?:
      | StrictTransition<TState, TAction, TGuard>
      | StrictTransition<TState, TAction, TGuard>[];
    onError?:
      | StrictTransition<TState, TAction, TGuard>
      | StrictTransition<TState, TAction, TGuard>[];
  };
  always?: StrictTransition<TState, TAction, TGuard>[];
};

export function defineMachine<TContext, TEvent extends { type: string }>() {
  return function <
    TState extends string,
    TAction extends string = never,
    TGuard extends string = never,
    TService extends string = never,
  >(definition: {
    initial: NoInfer<TState>;
    context?: TContext;
    states: {
      [key in TState]: StrictStateNode<
        TContext,
        TEvent,
        TState,
        TAction,
        TGuard,
        TService
      > &
        MachineStateTagsNode;
    };
    on?: StrictTransitions<TEvent, TState, TAction, TGuard>;
    actions?: Partial<Record<TAction, MachineAction<TContext, TEvent>>>;
    guards?: Partial<Record<TGuard, MachineGuard<TContext, TEvent>>>;
    services?: Partial<
      Record<TService, MachineServiceFunction<TContext, TEvent>>
    >;
  }): MachineDefinition<TContext, TEvent, TState, TAction, TGuard, TService> {
    return definition as MachineDefinition<
      TContext,
      TEvent,
      TState,
      TAction,
      TGuard,
      TService
    >;
  };
}
